'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppLayout from '@/components/common/AppLayout';
import { useAuth } from '@/contexts/AuthContext';
import { ALL_USER_ROLES, ROLE_DISPLAY_LABELS, type UserRole } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import PendingProvisioningPanel from './PendingProvisioningPanel';
import TempPasswordModal from './TempPasswordModal';

interface Module {
  id: string;
  label: string;
  icon: string;
  description: string;
  status: 'active' | 'coming_soon';
  path: string;
  estimatedDate?: string;
  roles: string[];
  permissions: ('view' | 'edit' | 'admin')[];
  activeUsers?: number;
  features: string[];
}

interface Role {
  id: string;
  name: string;
  color: string;
  bgColor: string;
  userCount: number;
  modules: string[];
  description: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  roles: string[];
  lastActive: string;
  status: 'online' | 'offline' | 'away';
  avatar: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  department?: string;
  is_disabled?: boolean;
  employee_id?: string | null;
}

interface AuditLogEntry {
  id: string;
  modified_by_id: string;
  modified_by_name: string;
  target_user_id: string;
  target_user_name: string;
  roles_added: string[];
  roles_removed: string[];
  previous_roles: string[];
  new_roles: string[];
  changed_at: string;
}

interface EditUserForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  department: string;
}

const modules: Module[] = [
  {
    id: 'project-manager',
    label: 'Project Manager',
    icon: 'Squares2X2Icon',
    description: 'Full project lifecycle management with job tracking, crew coordination, and customer communication tools.',
    status: 'active',
    path: '/project-manager-dashboard',
    roles: ['Project Manager', 'SuperAdmin'],
    permissions: ['view', 'edit', 'admin'],
    activeUsers: 8,
    features: ['Job Cards', 'Schedule', 'Crew Management', 'Customer Comms', 'Photo Approval'],
  },
  {
    id: 'schedule',
    label: 'Schedule',
    icon: 'CalendarDaysIcon',
    description: 'Interactive calendar and schedule management for crew assignments and job timelines.',
    status: 'active',
    path: '/interactive-calendar-schedule-management',
    roles: ['Project Manager', 'Operations', 'Logistics', 'SuperAdmin'],
    permissions: ['view', 'edit'],
    activeUsers: 12,
    features: ['Calendar View', 'Job Blocks', 'Crew Scheduling', 'Conflict Detection'],
  },
  {
    id: 'meetings',
    label: 'Meetings',
    icon: 'VideoCameraIcon',
    description: 'Virtual meetings and customer communication hub with video call scheduling.',
    status: 'active',
    path: '/virtual-meetings-customer-communication-hub',
    roles: ['Project Manager', 'Executive', 'SuperAdmin'],
    permissions: ['view', 'edit'],
    activeUsers: 5,
    features: ['Video Calls', 'Meeting Scheduler', 'Customer Portal', 'Recording'],
  },
  {
    id: 'photos',
    label: 'Photos',
    icon: 'PhotoIcon',
    description: 'Photo gallery and approval workflow system for job site documentation.',
    status: 'active',
    path: '/photo-gallery-approval-workflow-system',
    roles: ['Project Manager', 'Quality Control', 'Installer Crew', 'Warranty', 'SuperAdmin'],
    permissions: ['view', 'edit', 'admin'],
    activeUsers: 15,
    features: ['Gallery View', 'Approval Workflow', 'Customer Push', 'Batch Operations'],
  },
  {
    id: 'executive',
    label: 'Executive',
    icon: 'ChartBarSquareIcon',
    description: 'High-level business intelligence dashboard with KPIs, revenue metrics, and strategic insights for executive leadership.',
    status: 'coming_soon',
    path: '#',
    estimatedDate: 'Q2 2026',
    roles: ['Executive', 'SuperAdmin'],
    permissions: ['view'],
    features: ['KPI Dashboard', 'Revenue Analytics', 'Strategic Reports', 'Board Summaries'],
  },
  {
    id: 'financial',
    label: 'Financial',
    icon: 'BanknotesIcon',
    description: 'Comprehensive financial management including invoicing, payment tracking, and profit/loss reporting.',
    status: 'coming_soon',
    path: '#',
    estimatedDate: 'Q2 2026',
    roles: ['Financial', 'Executive', 'SuperAdmin'],
    permissions: ['view', 'edit', 'admin'],
    features: ['Invoice Management', 'Payment Tracking', 'P&L Reports', 'Budget Planning'],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: 'CogIcon',
    description: 'Operational workflow management for resource allocation, crew scheduling, and process optimization.',
    status: 'coming_soon',
    path: '#',
    estimatedDate: 'Q3 2026',
    roles: ['Operations', 'SuperAdmin'],
    permissions: ['view', 'edit'],
    features: ['Resource Allocation', 'Process Workflows', 'Efficiency Metrics', 'Team Management'],
  },
  {
    id: 'logistics',
    label: 'Logistics',
    icon: 'TruckIcon',
    description: 'Material delivery tracking, supplier management, and inventory control for roofing projects.',
    status: 'coming_soon',
    path: '#',
    estimatedDate: 'Q3 2026',
    roles: ['Logistics', 'Operations', 'SuperAdmin'],
    permissions: ['view', 'edit'],
    features: ['Delivery Tracking', 'Supplier Portal', 'Inventory Control', 'Order Management'],
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: 'CreditCardIcon',
    description: 'Customer billing, change order management, and payment processing with digital signature support.',
    status: 'coming_soon',
    path: '#',
    estimatedDate: 'Q3 2026',
    roles: ['Financial', 'Billing', 'SuperAdmin'],
    permissions: ['view', 'edit', 'admin'],
    features: ['Change Orders', 'Digital Signatures', 'Payment Processing', 'Billing History'],
  },
  {
    id: 'expansion',
    label: 'Expansion',
    icon: 'ArrowTrendingUpIcon',
    description: 'Business growth tools including market analysis, territory management, and expansion planning.',
    status: 'coming_soon',
    path: '#',
    estimatedDate: 'Q4 2026',
    roles: ['Executive', 'Strategy', 'SuperAdmin'],
    permissions: ['view'],
    features: ['Market Analysis', 'Territory Maps', 'Growth Metrics', 'Competitor Insights'],
  },
  {
    id: 'quality-control',
    label: 'Quality Control',
    icon: 'ClipboardDocumentCheckIcon',
    description: 'Inspection workflows, compliance tracking, and quality assurance for all roofing installations.',
    status: 'coming_soon',
    path: '#',
    estimatedDate: 'Q2 2026',
    roles: ['Quality Control', 'SuperAdmin'],
    permissions: ['view', 'edit'],
    features: ['Inspection Checklists', 'Compliance Reports', 'Photo Reviews', 'Defect Tracking'],
  },
  {
    id: 'installers',
    label: 'Installers',
    icon: 'WrenchScrewdriverIcon',
    description: 'Mobile-first installer portal for job assignments, photo uploads, and status updates from the field.',
    status: 'coming_soon',
    path: '#',
    estimatedDate: 'Q2 2026',
    roles: ['Installer Crew', 'SuperAdmin'],
    permissions: ['view', 'edit'],
    features: ['Job Assignments', 'Photo Upload', 'Status Updates', 'Time Tracking'],
  },
  {
    id: 'warranty',
    label: 'Warranty',
    icon: 'ShieldCheckIcon',
    description: 'Warranty registration, claims management, and customer service tracking for post-installation support.',
    status: 'coming_soon',
    path: '#',
    estimatedDate: 'Q4 2026',
    roles: ['Warranty', 'SuperAdmin'],
    permissions: ['view', 'edit'],
    features: ['Warranty Registration', 'Claims Processing', 'Customer Service', 'Expiry Tracking'],
  },
];

const roles: Role[] = [
  { id: 'superadmin', name: 'SuperAdmin', color: 'text-purple-700', bgColor: 'bg-purple-100', userCount: 2, modules: ['project-manager', 'schedule', 'meetings', 'photos', 'executive', 'financial', 'operations', 'logistics', 'billing', 'expansion', 'quality-control', 'installers', 'warranty'], description: 'Full system access with all module permissions' },
  { id: 'project-manager', name: 'Project Manager', color: 'text-cyan-700', bgColor: 'bg-cyan-100', userCount: 8, modules: ['project-manager', 'schedule', 'meetings', 'photos'], description: 'Active project management and crew coordination' },
  { id: 'executive', name: 'Executive', color: 'text-indigo-700', bgColor: 'bg-indigo-100', userCount: 3, modules: ['executive', 'financial', 'expansion', 'meetings'], description: 'High-level oversight and strategic reporting' },
  { id: 'financial', name: 'Financial', color: 'text-emerald-700', bgColor: 'bg-emerald-100', userCount: 4, modules: ['financial', 'billing'], description: 'Financial management and billing operations' },
  { id: 'operations', name: 'Operations', color: 'text-orange-700', bgColor: 'bg-orange-100', userCount: 5, modules: ['schedule', 'operations', 'logistics'], description: 'Resource allocation and operational workflows' },
  { id: 'quality-control', name: 'Quality Control', color: 'text-rose-700', bgColor: 'bg-rose-100', userCount: 4, modules: ['photos', 'quality-control'], description: 'Inspection and compliance management' },
  { id: 'logistics', name: 'Logistics', color: 'text-sky-700', bgColor: 'bg-sky-100', userCount: 4, modules: ['schedule', 'logistics'], description: 'Material delivery and inventory management' },
  { id: 'billing', name: 'Billing', color: 'text-lime-700', bgColor: 'bg-lime-100', userCount: 3, modules: ['billing', 'financial'], description: 'Customer billing and payment processing' },
  { id: 'expansion', name: 'Expansion', color: 'text-violet-700', bgColor: 'bg-violet-100', userCount: 2, modules: ['expansion', 'executive'], description: 'Business growth and territory management' },
  { id: 'installer-crew', name: 'Installer Crew', color: 'text-amber-700', bgColor: 'bg-amber-100', userCount: 24, modules: ['photos', 'installers'], description: 'Field work, photo uploads, and status updates' },
  { id: 'warranty', name: 'Warranty', color: 'text-teal-700', bgColor: 'bg-teal-100', userCount: 3, modules: ['warranty'], description: 'Post-installation warranty and claims support' },
];

const permissionColors: Record<string, string> = {
  view: 'bg-blue-100 text-blue-700',
  edit: 'bg-amber-100 text-amber-700',
  admin: 'bg-purple-100 text-purple-700',
};

const statusColors: Record<string, string> = {
  online: 'bg-emerald-500',
  away: 'bg-amber-500',
  offline: 'bg-slate-300',
};

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return iso;
  }
}

export default function RoleManagementInteractive() {
  const { currentUser, setCurrentUser } = useAuth();
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  const [activeTab, setActiveTab] = useState<'modules' | 'roles' | 'users' | 'audit'>('modules');
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [userList, setUserList] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [roleUserCounts, setRoleUserCounts] = useState<Record<string, number>>({});
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [draftRoles, setDraftRoles] = useState<string[]>([]);
  const [savingRoles, setSavingRoles] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Edit User Drawer state
  const [drawerUser, setDrawerUser] = useState<User | null>(null);
  const [drawerForm, setDrawerForm] = useState<EditUserForm>({ firstName: '', lastName: '', email: '', phone: '', department: '' });
  const [drawerRoles, setDrawerRoles] = useState<UserRole[]>([]);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [resendingInvite, setResendingInvite] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Set Password state
  const [pwForm, setPwForm] = useState({ password: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  // Audit log state
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');
  const [auditDateFrom, setAuditDateFrom] = useState('');
  const [auditDateTo, setAuditDateTo] = useState('');

  // Reset credentials modal (for shield icon password reset)
  const [resetCredentialsModal, setResetCredentialsModal] = useState<any>(null);

  // Role Distribution state
  const [roleDistribution, setRoleDistribution] = useState<Array<{ role: string; provisioned: number; assigned: number }>>([]);

  // KPI counts state
  const [kpiCounts, setKpiCounts] = useState({
    totalUsers: 0,
    pendingProvisioning: 0,
    totalRoles: ALL_USER_ROLES.length,
    activeUsers: 0,
  });

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const { data, error } = await supabase
        .from('role_audit_log')
        .select('*')
        .order('changed_at', { ascending: false });
      if (!error && data) {
        setAuditLog(data as AuditLogEntry[]);
      }
    } catch {
      // silently fail
    } finally {
      setAuditLoading(false);
    }
  }, [supabase]);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      // Fetch directly from user_profiles to get is_disabled and employee_id
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, phone, department, employee_id, user_role, user_roles, profile_image, is_disabled, disabled_at, updated_at')
        .order('first_name');

      if (error) {
        console.error('[fetchUsers] Error:', error);
        setUsersLoading(false);
        return;
      }

      if (Array.isArray(data)) {
        const mapped: User[] = data.map((u: any) => {
          const firstName = u.first_name ?? '';
          const lastName = u.last_name ?? '';
          const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '??';
          const userRoles: string[] = Array.isArray(u.user_roles)
            ? u.user_roles.map((r: string) => r.replace(/_/g, ' '))
            : u.user_role ? [u.user_role.replace(/_/g, ' ')] : [];
          return {
            id: u.id,
            name: `${firstName} ${lastName}`.trim() || u.email,
            email: u.email ?? '',
            roles: userRoles,
            lastActive: u.updated_at ? new Date(u.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—',
            status: 'offline' as const,
            avatar: initials,
            firstName,
            lastName,
            phone: u.phone ?? '',
            department: u.department ?? '',
            is_disabled: u.is_disabled ?? false,
            employee_id: u.employee_id ?? null,
          };
        });
        setUserList(mapped);

        // Compute real user counts per role
        const counts: Record<string, number> = {};
        data.forEach((u: any) => {
          if (Array.isArray(u.user_roles)) {
            u.user_roles.forEach((r: string) => {
              const roleId = r.toLowerCase().replace(/[\s_]+/g, '-');
              counts[roleId] = (counts[roleId] ?? 0) + 1;
            });
          }
        });
        setRoleUserCounts(counts);
      }
    } catch (err) {
      console.error('[fetchUsers] Network error:', err);
    } finally {
      setUsersLoading(false);
    }
  }, [supabase]);

  // Load role distribution with real data
  useEffect(() => {
    const loadDistribution = async () => {
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_roles, user_role');
      const { data: employees } = await supabase
        .from('employees')
        .select('roles');

      const dist: Record<string, { provisioned: number; assigned: number }> = {};
      ALL_USER_ROLES.forEach(r => { dist[r] = { provisioned: 0, assigned: 0 }; });

      (profiles ?? []).forEach((p: any) => {
        const roleArr = Array.isArray(p.user_roles) && p.user_roles.length > 0 ? p.user_roles : (p.user_role ? [p.user_role] : []);
        roleArr.forEach((r: string) => { if (dist[r]) dist[r].provisioned += 1; });
      });

      (employees ?? []).forEach((e: any) => {
        (e.roles ?? []).forEach((r: string) => { if (dist[r]) dist[r].assigned += 1; });
      });

      setRoleDistribution(ALL_USER_ROLES.map(r => ({ role: r, ...dist[r] })));
    };
    loadDistribution();
  }, [reloadKey, supabase]);

  // Load KPI counts
  useEffect(() => {
    const loadKpis = async () => {
      const { count: totalUsers } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true });

      const { count: activeUsers } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('is_disabled', false);

      const { data: allEmps } = await supabase.from('employees').select('id');
      const { data: provProfiles } = await supabase.from('user_profiles').select('employee_id').not('employee_id', 'is', null);
      const provIds = new Set((provProfiles ?? []).map((p: any) => p.employee_id));
      const pending = (allEmps ?? []).filter((e: any) => !provIds.has(e.id)).length;

      setKpiCounts({
        totalUsers: totalUsers ?? 0,
        activeUsers: activeUsers ?? 0,
        pendingProvisioning: pending,
        totalRoles: ALL_USER_ROLES.length,
      });
    };
    loadKpis();
  }, [reloadKey, supabase]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, reloadKey]);

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditLog();
    }
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab, fetchAuditLog, fetchUsers]);

  // Real-time subscription for audit log
  useEffect(() => {
    const channel = supabase
      .channel('role_audit_log_realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'role_audit_log' },
        (payload) => {
          setAuditLog(prev => [payload.new as AuditLogEntry, ...prev]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase]);

  // Edit User Drawer handlers
  const openEditDrawer = (user: User) => {
    setDrawerUser(user);
    setDrawerForm({
      firstName: user.firstName ?? user.name.split(' ')[0] ?? '',
      lastName: user.lastName ?? user.name.split(' ').slice(1).join(' ') ?? '',
      email: user.email,
      phone: user.phone ?? '',
      department: user.department ?? '',
    });
    // Populate roles from user_roles array; fallback to first role
    const rawRoles = user.roles.length > 0 ? user.roles : [];
    const normalized = rawRoles.map(r => r.replace(/\s+/g, '_')) as UserRole[];
    setDrawerRoles(normalized.filter(r => ALL_USER_ROLES.includes(r)));
    setDrawerError(null);
  };

  const closeEditDrawer = () => {
    setDrawerUser(null);
    setDrawerError(null);
    setPwForm({ password: '', confirm: '' });
    setPwError(null);
  };

  const savePassword = async () => {
    if (!drawerUser) return;
    if (pwForm.password.length < 6) {
      setPwError('Password must be at least 6 characters.');
      return;
    }
    if (pwForm.password !== pwForm.confirm) {
      setPwError('Passwords do not match.');
      return;
    }
    setPwSaving(true);
    setPwError(null);
    try {
      const res = await fetch('/api/users/set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: drawerUser.email, password: pwForm.password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPwError(data.error ?? 'Failed to update password.');
        setPwSaving(false);
        return;
      }
      setPwForm({ password: '', confirm: '' });
      showToast(`Password updated for ${drawerUser.name}`);
    } catch {
      setPwError('Network error. Please try again.');
    }
    setPwSaving(false);
  };

  const saveEditDrawer = async () => {
    if (!drawerUser) return;
    if (drawerRoles.length === 0) {
      setDrawerError('Select at least one role.');
      return;
    }
    setDrawerSaving(true);
    setDrawerError(null);

    const ROLE_PRIORITY: UserRole[] = ['Admin', 'Manager', 'Sales'];
    const primaryRole = ROLE_PRIORITY.find(r => drawerRoles.includes(r)) ?? drawerRoles[0];

    const payload: any = {
      first_name: drawerForm.firstName,
      last_name: drawerForm.lastName,
      email: drawerForm.email,
      phone: drawerForm.phone || null,
      department: drawerForm.department || null,
      user_role: primaryRole,
      user_roles: drawerRoles,
      updated_at: new Date().toISOString(),
    };

    console.log(`[UserEdit] Saving user ${drawerUser.id}:`, payload);

    for (let attempt = 0; attempt < 3; attempt++) {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Save timed out (attempt ${attempt + 1}/3)`)), 8000));
      const opPromise = supabase
        .from('user_profiles')
        .update(payload)
        .eq('id', drawerUser.id)
        .then((res: any) => { if (res.error) throw res.error; return res.data; });

      try {
        await Promise.race([opPromise, timeoutPromise]);
        console.log(`[UserEdit] Save succeeded attempt ${attempt + 1}`);

        // Update local list
        setUserList(prev =>
          prev.map(u =>
            u.id === drawerUser.id
              ? {
                  ...u,
                  name: `${drawerForm.firstName} ${drawerForm.lastName}`.trim(),
                  email: drawerForm.email,
                  phone: drawerForm.phone,
                  department: drawerForm.department,
                  firstName: drawerForm.firstName,
                  lastName: drawerForm.lastName,
                  roles: drawerRoles.map(r => r.replace(/_/g, ' ')),
                  avatar: `${drawerForm.firstName.charAt(0)}${drawerForm.lastName.charAt(0)}`.toUpperCase(),
                }
              : u
          )
        );

        // If editing the currently logged-in user, update AuthContext
        const editedEmail = drawerForm.email.toLowerCase().trim();
        const loggedInEmail = (currentUser.email ?? '').toLowerCase().trim();
        if (editedEmail && loggedInEmail && editedEmail === loggedInEmail) {
          setCurrentUser({
            ...currentUser,
            first_name: drawerForm.firstName,
            last_name: drawerForm.lastName,
            role: primaryRole,
            roles: drawerRoles,
          });
        }

        setDrawerSaving(false);
        closeEditDrawer();
        showToast('User profile updated successfully.');
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
        const isTimeout = msg.includes('timed out');
        console.warn(`[UserEdit] Save attempt ${attempt + 1} failed: ${msg}`);
        if (attempt === 2 || (!isRateLimit && !isTimeout)) {
          setDrawerError(`Save failed: ${msg}`);
          setDrawerSaving(false);
          return;
        }
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
      }
    }
  };

  const resendInvite = async () => {
    if (!drawerUser) return;
    setResendingInvite(true);
    try {
      const res = await fetch('/api/users/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: drawerForm.email,
          firstName: drawerForm.firstName,
          lastName: drawerForm.lastName,
          role: drawerRoles[0] ?? 'Project_Manager',
        }),
      });
      if (res.ok) {
        showToast(`Invite resent to ${drawerForm.email}`);
      } else {
        const data = await res.json();
        showToast(data.error ?? 'Failed to resend invite.', 'error');
      }
    } catch {
      showToast('Failed to resend invite.', 'error');
    } finally {
      setResendingInvite(false);
    }
  };

  const handleResetPassword = async (user: User) => {
    if (!confirm(`Reset password for ${user.firstName} ${user.lastName}? Their current password will be invalidated.`)) return;
    try {
      const r = await fetch('/api/rbac/admin-reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id }),
      });
      const data = await r.json();
      if (!r.ok || data.error) {
        showToast(`Reset failed: ${data.error ?? 'Unknown'}`, 'error');
        return;
      }
      setResetCredentialsModal({
        action: 'reset',
        user_name: data.user_name,
        email: data.email,
        temp_password: data.temp_password,
        message: data.message,
      });
    } catch (err) {
      showToast(`Reset failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  };

  const handleToggleDisabled = async (user: User) => {
    const isDisabled = user.is_disabled === true;
    let reason: string | null = null;
    if (!isDisabled) {
      const input = prompt(`Why are you disabling ${user.firstName}'s login? (optional, press Cancel to abort)`);
      if (input === null) return; // user cancelled
      reason = input.trim() || null;
    } else {
      if (!confirm(`Re-enable ${user.firstName}'s login?`)) return;
    }

    try {
      const r = await fetch('/api/rbac/toggle-user-disabled', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, disable: !isDisabled, reason }),
      });
      const data = await r.json();
      if (!r.ok || data.error) {
        showToast(`${isDisabled ? 'Enable' : 'Disable'} failed: ${data.error ?? 'Unknown'}`, 'error');
        return;
      }
      showToast(`${user.firstName}'s login ${isDisabled ? 'enabled' : 'disabled'}`);
      // Update local list immediately
      setUserList(prev =>
        prev.map(u => u.id === user.id ? { ...u, is_disabled: !isDisabled } : u)
      );
      setReloadKey(k => k + 1);
    } catch (err) {
      showToast(`Failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  };

  const openUserModal = (user: User) => {
    setEditingUser(user);
    setDraftRoles([...user.roles]);
  };

  const closeUserModal = () => {
    setEditingUser(null);
    setDraftRoles([]);
  };

  const toggleDraftRole = (roleName: string) => {
    setDraftRoles(prev =>
      prev.includes(roleName) ? prev.filter(r => r !== roleName) : [...prev, roleName]
    );
  };

  const saveUserRoles = async () => {
    if (!editingUser) return;
    setSavingRoles(true);
    setSaveError(null);

    const previousRoles = [...editingUser.roles];
    const newRoles = [...draftRoles];
    const rolesAdded = newRoles.filter(r => !previousRoles.includes(r));
    const rolesRemoved = previousRoles.filter(r => !newRoles.includes(r));

    // Persist role changes to user_profiles table
    const rolesForDb = newRoles.map(r => r.replace(/\s+/g, '_'));
    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({
        user_roles: rolesForDb,
        user_role: rolesForDb[0] ?? 'Project_Manager',
        updated_at: new Date().toISOString(),
      })
      .eq('id', editingUser.id);

    if (updateError) {
      setSaveError('Failed to save roles. Please try again.');
      setSavingRoles(false);
      return;
    }

    // Write audit log entry to Supabase
    try {
      await supabase.from('role_audit_log').insert({
        modified_by_id: currentUser.id,
        modified_by_name: `${currentUser.first_name} ${currentUser.last_name}`.trim() || currentUser.email,
        target_user_id: editingUser.id,
        target_user_name: editingUser.name,
        roles_added: rolesAdded,
        roles_removed: rolesRemoved,
        previous_roles: previousRoles,
        new_roles: newRoles,
        changed_at: new Date().toISOString(),
      });
    } catch {
      // silently continue — role update already succeeded
    }

    // If the edited user is the currently logged-in user, update AuthContext
    const editedEmail = (editingUser.email ?? '').toLowerCase().trim();
    const loggedInEmail = (currentUser.email ?? '').toLowerCase().trim();
    if (editedEmail && loggedInEmail && editedEmail === loggedInEmail) {
      const updatedRoles = newRoles.map(r => r.replace(/\s+/g, '_')) as import('@/contexts/AuthContext').UserRole[];
      setCurrentUser({
        ...currentUser,
        role: updatedRoles[0] ?? currentUser.role,
        roles: updatedRoles,
      });
    }

    // Update local user list to reflect saved changes
    setUserList(prev =>
      prev.map(u => u.id === editingUser.id ? { ...u, roles: newRoles } : u)
    );
    setSavingRoles(false);
    closeUserModal();
  };

  const filteredModules = modules.filter(m => {
    const matchesSearch = m.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === 'all' || m.roles.some(r => r.toLowerCase().replace(/\s+/g, '-') === selectedRole);
    return matchesSearch && matchesRole;
  });

  const activeModules = filteredModules.filter(m => m.status === 'active');
  const comingSoonModules = filteredModules.filter(m => m.status === 'coming_soon');

  // Filtered audit log
  const filteredAuditLog = auditLog.filter(entry => {
    const matchesSearch = auditSearch === '' ||
      entry.modified_by_name.toLowerCase().includes(auditSearch.toLowerCase()) ||
      entry.target_user_name.toLowerCase().includes(auditSearch.toLowerCase());
    const entryDate = new Date(entry.changed_at);
    const matchesFrom = auditDateFrom === '' || entryDate >= new Date(auditDateFrom);
    const matchesTo = auditDateTo === '' || entryDate <= new Date(auditDateTo + 'T23:59:59');
    return matchesSearch && matchesFrom && matchesTo;
  });

  const maxRoleCount = Math.max(...roleDistribution.map(r => Math.max(r.provisioned, r.assigned)), 1);

  return (
    <AppLayout>
      <div className="p-6 max-w-[1400px] mx-auto">

        {/* Toast Notification */}
        {toast && (
          <div className={`fixed top-5 right-5 z-[100] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all duration-300 ${
            toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} size={18} className={toast.type === 'success' ? 'text-emerald-500' : 'text-red-500'} variant="solid" />
            {toast.message}
            <button suppressHydrationWarning onClick={() => setToast(null)} className="ml-2 text-current opacity-60 hover:opacity-100">
              <Icon name="XMarkIcon" size={16} />
            </button>
          </div>
        )}

        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <Icon name="HomeIcon" size={14} />
            <span>/</span>
            <span className="text-foreground font-medium">Role Management</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Role-Based Navigation & Module Management</h1>
              <p className="text-muted-foreground text-sm mt-1">Manage user roles, module access, and permission levels across the NextRoof 360 platform.</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {currentUser.roles?.includes('Admin') && (
                <>
                  <Link href="/rbac-dashboard-schema-discovery" className="text-xs text-muted-foreground hover:text-[#E89A3C] transition-standard underline underline-offset-2 whitespace-nowrap">
                    Schema Discovery
                  </Link>
                  <Link href="/rbac-dashboard-filter-smoke-test" className="text-xs text-muted-foreground hover:text-[#E89A3C] transition-standard underline underline-offset-2 whitespace-nowrap">
                    Filter Smoke Test
                  </Link>
                </>
              )}
              <Link href="/add-user-management-page" className="flex items-center gap-2 px-4 py-2 bg-[#E89A3C] text-white rounded-lg text-sm font-medium hover:bg-[#0599cc] transition-standard press-scale hover-lift whitespace-nowrap">
                <Icon name="PlusIcon" size={16} variant="solid" />
                Add User
              </Link>
            </div>
          </div>
        </div>

        {/* Current User Multi-Role Banner */}
        <div className="mb-6 bg-[#1C2E5E]/5 border border-[#1C2E5E]/20 rounded-xl p-4 flex flex-wrap items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#1C2E5E] flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-bold font-heading">{currentUser.first_name.charAt(0)}{currentUser.last_name.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">{currentUser.first_name} {currentUser.last_name}</p>
            <p className="text-xs text-muted-foreground">{currentUser.email}</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {currentUser.roles.map((r) => {
              const roleData = roles.find(rd => rd.name.toLowerCase().replace(/\s+/g, '-') === r.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-'));
              return (
                <span key={r} className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleData ? `${roleData.bgColor} ${roleData.color}` : 'bg-slate-100 text-slate-700'}`}>
                  {r.replace(/_/g, ' ')}
                </span>
              );
            })}
          </div>
        </div>

        {/* Stats Row — live KPI tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-xl border border-border p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0">
              <Icon name="UsersIcon" size={20} className="text-[#E89A3C]" />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold text-foreground">{kpiCounts.totalUsers}</p>
              <p className="text-xs text-muted-foreground">Total Users</p>
            </div>
          </div>
          <div className="bg-card rounded-xl border border-border p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <Icon name="CheckCircleIcon" size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold text-foreground">{kpiCounts.activeUsers}</p>
              <p className="text-xs text-muted-foreground">Active Users</p>
            </div>
          </div>
          <div className={`bg-card rounded-xl border p-4 flex items-center gap-3 ${kpiCounts.pendingProvisioning > 0 ? 'border-amber-300' : 'border-border'}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${kpiCounts.pendingProvisioning > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
              <Icon name="ClockIcon" size={20} className={kpiCounts.pendingProvisioning > 0 ? 'text-amber-600' : 'text-gray-400'} />
            </div>
            <div>
              <p className={`text-2xl font-heading font-bold ${kpiCounts.pendingProvisioning > 0 ? 'text-amber-700' : 'text-foreground'}`}>{kpiCounts.pendingProvisioning}</p>
              <p className="text-xs text-muted-foreground">Pending Provisioning</p>
            </div>
          </div>
          <div className="bg-card rounded-xl border border-border p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
              <Icon name="ShieldCheckIcon" size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold text-foreground">{kpiCounts.totalRoles}</p>
              <p className="text-xs text-muted-foreground">User Roles</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-lg mb-6 w-fit">
          {(['modules', 'roles', 'users', 'audit'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              suppressHydrationWarning
              className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-standard ${
                activeTab === tab ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'modules' ? 'Modules' : tab === 'roles' ? 'Roles & Permissions' : tab === 'users' ? 'Users' : 'Audit Log'}
            </button>
          ))}
        </div>

        {/* MODULES TAB */}
        {activeTab === 'modules' && (
          <div className="flex gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <div className="relative flex-1">
                  <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    suppressHydrationWarning
                    type="text"
                    placeholder="Search modules..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] focus:border-transparent"
                  />
                </div>
                <select
                  suppressHydrationWarning
                  value={selectedRole}
                  onChange={e => setSelectedRole(e.target.value)}
                  className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] text-foreground"
                >
                  <option value="all">All Roles</option>
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>

              {activeModules.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Active Modules ({activeModules.length})</h2>
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                    {activeModules.map(mod => (
                      <ModuleCard key={mod.id} module={mod} isSelected={selectedModule?.id === mod.id} onClick={() => setSelectedModule(selectedModule?.id === mod.id ? null : mod)} />
                    ))}
                  </div>
                </div>
              )}

              {comingSoonModules.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                    <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Coming Soon ({comingSoonModules.length})</h2>
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                    {comingSoonModules.map(mod => (
                      <ModuleCard key={mod.id} module={mod} isSelected={selectedModule?.id === mod.id} onClick={() => setSelectedModule(selectedModule?.id === mod.id ? null : mod)} />
                    ))}
                  </div>
                </div>
              )}

              {filteredModules.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">
                  <Icon name="MagnifyingGlassIcon" size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No modules found</p>
                  <p className="text-sm">Try adjusting your search or filter</p>
                </div>
              )}
            </div>

            {selectedModule && (
              <div className="w-80 flex-shrink-0">
                <div className="bg-card border border-border rounded-xl p-5 sticky top-[76px]">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        selectedModule.status === 'active' ? 'bg-[#E89A3C]/10' : 'bg-muted'
                      }`}>
                        <Icon name={selectedModule.icon as any} size={20} className={selectedModule.status === 'active' ? 'text-[#E89A3C]' : 'text-muted-foreground'} />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-foreground">{selectedModule.label}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${selectedModule.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {selectedModule.status === 'active' ? 'Active' : `Coming ${selectedModule.estimatedDate}`}
                        </span>
                      </div>
                    </div>
                    <button suppressHydrationWarning onClick={() => setSelectedModule(null)} className="text-muted-foreground hover:text-foreground transition-standard p-1">
                      <Icon name="XMarkIcon" size={18} />
                    </button>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4">{selectedModule.description}</p>

                  {selectedModule.activeUsers !== undefined && (
                    <div className="flex items-center gap-2 mb-4 p-3 bg-muted rounded-lg">
                      <Icon name="UsersIcon" size={16} className="text-[#E89A3C]" />
                      <span className="text-sm text-foreground"><strong>{selectedModule.activeUsers}</strong> active users</span>
                    </div>
                  )}

                  <div className="mb-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Permissions</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedModule.permissions.map(p => (
                        <span key={p} className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${permissionColors[p]}`}>{p}</span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Authorized Roles</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedModule.roles.map(r => (
                        <span key={r} className="text-xs px-2 py-0.5 rounded-full bg-[#1C2E5E]/10 text-[#1C2E5E] font-medium">{r}</span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Features</p>
                    <ul className="space-y-1.5">
                      {selectedModule.features.map(f => (
                        <li key={f} className="flex items-center gap-2 text-sm text-foreground">
                          <Icon name="CheckCircleIcon" size={14} className={selectedModule.status === 'active' ? 'text-emerald-500' : 'text-muted-foreground'} variant="solid" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {selectedModule.status === 'active' ? (
                    <Link href={selectedModule.path} className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#E89A3C] text-white rounded-lg text-sm font-medium hover:bg-[#0599cc] transition-standard press-scale">
                      <Icon name="ArrowTopRightOnSquareIcon" size={16} />
                      Open Module
                    </Link>
                  ) : (
                    <button suppressHydrationWarning className="flex items-center justify-center gap-2 w-full py-2.5 bg-muted text-muted-foreground rounded-lg text-sm font-medium cursor-not-allowed" disabled>
                      <Icon name="ClockIcon" size={16} />
                      Available {selectedModule.estimatedDate}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ROLES TAB */}
        {activeTab === 'roles' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {roles.map(role => (
                <div key={role.id} className="bg-card border border-border rounded-xl p-5 hover:shadow-md transition-standard">
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg ${role.bgColor} flex items-center justify-center`}>
                      <Icon name="ShieldCheckIcon" size={18} className={role.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-heading font-semibold text-foreground">{role.name}</h3>
                        <span className={`text-xs font-medium ${role.color}`}>{roleUserCounts[role.id] ?? role.userCount} users</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">{role.description}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {role.modules.slice(0, 5).map(modId => {
                          const mod = modules.find(m => m.id === modId);
                          return mod ? (
                            <span key={modId} className={`text-xs px-2 py-0.5 rounded-full font-medium ${mod.status === 'active' ? 'bg-[#E89A3C]/10 text-[#E89A3C]' : 'bg-muted text-muted-foreground'}`}>
                              {mod.label}
                            </span>
                          ) : null;
                        })}
                        {role.modules.length > 5 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">+{role.modules.length - 5} more</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Permission Matrix */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-border">
                <h2 className="font-heading font-semibold text-foreground">Permission Matrix</h2>
                <p className="text-sm text-muted-foreground">Overview of role access levels across active modules</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted">
                      <th className="text-left px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide w-40">Role</th>
                      {modules.filter(m => m.status === 'active').map(m => (
                        <th key={m.id} className="text-center px-3 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">{m.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {roles.map(role => (
                      <tr key={role.id} className="hover:bg-muted/50 transition-standard">
                        <td className="px-5 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${role.bgColor} ${role.color}`}>{role.name}</span>
                        </td>
                        {modules.filter(m => m.status === 'active').map(m => {
                          const hasAccess = role.modules.includes(m.id);
                          return (
                            <td key={m.id} className="text-center px-3 py-3">
                              {hasAccess ? (
                                <Icon name="CheckCircleIcon" size={18} className="text-emerald-500 mx-auto" variant="solid" />
                              ) : (
                                <Icon name="MinusCircleIcon" size={18} className="text-slate-200 mx-auto" variant="solid" />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Pending Provisioning Panel — rendered ABOVE System Users */}
            <PendingProvisioningPanel onProvisioned={() => setReloadKey(k => k + 1)} />

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  className="w-full pl-9 pr-4 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] focus:border-transparent"
                />
              </div>
              <select className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] text-foreground">
                <option value="">All Roles</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <select className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] text-foreground">
                <option value="">All Status</option>
                <option value="online">Online</option>
                <option value="away">Away</option>
                <option value="offline">Offline</option>
              </select>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <h2 className="font-heading font-semibold text-foreground">System Users</h2>
                <span className="text-sm text-muted-foreground">{userList.length} users</span>
              </div>
              {usersLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="flex flex-col items-center gap-3 text-muted-foreground">
                    <div className="w-8 h-8 border-2 border-[#E89A3C] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm">Loading users...</p>
                  </div>
                </div>
              ) : userList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Icon name="UsersIcon" size={40} className="mb-3 opacity-30" />
                  <p className="font-medium">No users found</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {userList.map(user => (
                    <div
                      key={user.id}
                      className={`flex items-center gap-4 px-5 py-4 hover:bg-muted/50 transition-standard cursor-pointer ${user.is_disabled ? 'opacity-60' : ''}`}
                      onClick={() => openUserModal(user)}
                    >
                      <div className="relative flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-[#1C2E5E] flex items-center justify-center">
                          <span className="text-white text-sm font-semibold font-heading">{user.avatar}</span>
                        </div>
                        <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-card ${statusColors[user.status]}`}></span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 flex-wrap">
                          <p className="font-medium text-foreground text-sm">{user.name}</p>
                          {user.is_disabled && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold bg-red-100 text-red-700 ml-1">DISABLED</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                      <div className="hidden sm:flex flex-wrap gap-1 max-w-[180px]">
                        {user.roles.map(r => {
                          const roleData = roles.find(rd => rd.name === r);
                          return (
                            <span key={r} className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${roleData ? `${roleData.bgColor} ${roleData.color}` : 'bg-slate-100 text-slate-700'}`}>
                              {r}
                            </span>
                          );
                        })}
                      </div>
                      <div className="hidden md:block text-right">
                        <p className="text-xs text-muted-foreground">{user.lastActive}</p>
                        <p className={`text-xs font-medium capitalize ${user.status === 'online' ? 'text-emerald-600' : user.status === 'away' ? 'text-amber-600' : 'text-slate-400'}`}>{user.status}</p>
                      </div>
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        <button
                          suppressHydrationWarning
                          onClick={() => openEditDrawer(user)}
                          className="flex-shrink-0 p-1.5 text-[#E89A3C] hover:bg-cyan-50 rounded-md transition-standard"
                          title="Edit user profile"
                        >
                          <Icon name="PencilSquareIcon" size={16} />
                        </button>
                        <button
                          suppressHydrationWarning
                          onClick={() => handleResetPassword(user)}
                          className="flex-shrink-0 p-1.5 text-[#1C2E5E] hover:bg-blue-50 rounded-md transition-standard"
                          title="Reset password (admin)"
                        >
                          <Icon name="ShieldCheckIcon" size={16} />
                        </button>
                        <button
                          suppressHydrationWarning
                          onClick={() => handleToggleDisabled(user)}
                          className={`flex-shrink-0 p-1.5 rounded-md transition-standard ${user.is_disabled ? 'text-emerald-600 hover:bg-emerald-50' : 'text-error hover:bg-red-50'}`}
                          title={user.is_disabled ? 'Re-enable login' : 'Disable login'}
                        >
                          <Icon name="NoSymbolIcon" size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="px-5 py-3 border-t border-border bg-muted/30 flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Showing {userList.length} users</p>
                <div className="flex items-center gap-1">
                  <button suppressHydrationWarning className="px-3 py-1.5 text-xs bg-card border border-border rounded-md text-muted-foreground hover:text-foreground transition-standard">Previous</button>
                  <button suppressHydrationWarning className="px-3 py-1.5 text-xs bg-[#E89A3C] text-white rounded-md hover:bg-[#0599cc] transition-standard">Next</button>
                </div>
              </div>
            </div>

            {/* Role Distribution — 16 roles with real data */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h3 className="text-lg font-bold text-[#1C2E5E] mb-1">Role Distribution ({ALL_USER_ROLES.length} Roles)</h3>
              <p className="text-[11px] text-gray-500 mb-4">Provisioned = users with logins · Assigned = roles set on employee records (not yet provisioned)</p>
              <div className="space-y-2">
                {roleDistribution.map(({ role, provisioned, assigned }) => (
                  <div key={role} className="flex items-center gap-3">
                    <span className="text-[12px] text-gray-700 w-40 truncate flex-shrink-0">
                      {ROLE_DISPLAY_LABELS[role as UserRole] ?? role}
                    </span>
                    <div className="flex-1 flex items-center gap-1">
                      <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden relative">
                        {provisioned > 0 && (
                          <div
                            className="h-full bg-blue-500 absolute top-0 left-0"
                            style={{ width: `${(provisioned / maxRoleCount) * 100}%` }}
                          />
                        )}
                        {assigned > provisioned && (
                          <div
                            className="h-full bg-amber-300 absolute top-0"
                            style={{
                              left: `${(provisioned / maxRoleCount) * 100}%`,
                              width: `${((assigned - provisioned) / maxRoleCount) * 100}%`,
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <span className="text-[11px] text-gray-600 w-16 text-right flex-shrink-0">
                      {provisioned} / {assigned}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-4 text-[10px]">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-blue-500 inline-block" />
                  Provisioned
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-amber-300 inline-block" />
                  Assigned (no login yet)
                </span>
              </div>
            </div>
          </div>
        )}

        {/* AUDIT LOG TAB */}
        {activeTab === 'audit' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    suppressHydrationWarning
                    type="text"
                    placeholder="Search by user name..."
                    value={auditSearch}
                    onChange={e => setAuditSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-[#F8F9FA] border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="CalendarDaysIcon" size={16} className="text-muted-foreground flex-shrink-0" />
                  <input
                    suppressHydrationWarning
                    type="date"
                    value={auditDateFrom}
                    onChange={e => setAuditDateFrom(e.target.value)}
                    className="px-3 py-2 bg-[#F8F9FA] border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] text-foreground"
                  />
                  <span className="text-muted-foreground text-sm">to</span>
                  <input
                    suppressHydrationWarning
                    type="date"
                    value={auditDateTo}
                    onChange={e => setAuditDateTo(e.target.value)}
                    className="px-3 py-2 bg-[#F8F9FA] border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] text-foreground"
                  />
                </div>
                {(auditSearch || auditDateFrom || auditDateTo) && (
                  <button
                    suppressHydrationWarning
                    onClick={() => { setAuditSearch(''); setAuditDateFrom(''); setAuditDateTo(''); }}
                    className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground bg-muted rounded-lg border border-border transition-standard flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <Icon name="XMarkIcon" size={14} />
                    Clear
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-muted-foreground">Live — new entries appear automatically</span>
                <span className="ml-auto text-xs text-muted-foreground">{filteredAuditLog.length} {filteredAuditLog.length === 1 ? 'entry' : 'entries'}</span>
              </div>
            </div>

            {/* Audit Table */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <div>
                  <h2 className="font-heading font-semibold text-foreground">Role Change History</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Complete record of all role modifications</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-lg">
                  <Icon name="ClockIcon" size={13} />
                  Real-time
                </div>
              </div>

              {auditLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="flex flex-col items-center gap-3 text-muted-foreground">
                    <div className="w-8 h-8 border-2 border-[#E89A3C] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm">Loading audit log...</p>
                  </div>
                </div>
              ) : filteredAuditLog.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Icon name="ClipboardDocumentListIcon" size={40} className="mb-3 opacity-30" />
                  <p className="font-medium">No audit entries found</p>
                  <p className="text-sm">
                    {auditSearch || auditDateFrom || auditDateTo
                      ? 'Try adjusting your filters' : 'Role changes will appear here'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#F8F9FA] border-b border-border">
                        <th className="text-left px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">Date / Time</th>
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">Modified By</th>
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">Affected User</th>
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">Roles Added</th>
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">Roles Removed</th>
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap">Summary</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredAuditLog.map(entry => {
                        const addedCount = entry.roles_added?.length ?? 0;
                        const removedCount = entry.roles_removed?.length ?? 0;
                        const diffSummary = addedCount === 0 && removedCount === 0
                          ? 'No changes'
                          : [
                              addedCount > 0 ? `+${addedCount} added` : '',
                              removedCount > 0 ? `−${removedCount} removed` : '',
                            ].filter(Boolean).join(', ');

                        return (
                          <tr key={entry.id} className="hover:bg-[#F8F9FA]/60 transition-standard">
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <p className="text-xs font-medium text-foreground">{formatDateTime(entry.changed_at)}</p>
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-[#1C2E5E] flex items-center justify-center flex-shrink-0">
                                  <span className="text-white text-[10px] font-bold font-heading">
                                    {entry.modified_by_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                                  </span>
                                </div>
                                <span className="text-sm text-foreground font-medium whitespace-nowrap">{entry.modified_by_name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                                  <span className="text-slate-600 text-[10px] font-bold font-heading">
                                    {entry.target_user_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                                  </span>
                                </div>
                                <span className="text-sm text-foreground whitespace-nowrap">{entry.target_user_name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="flex flex-wrap gap-1">
                                {entry.roles_added?.length > 0 ? (
                                  entry.roles_added.map((r: string) => (
                                    <span key={r} className="inline-flex items-center gap-0.5 text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                                      <span className="text-emerald-500 font-bold">+</span>{r}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="flex flex-wrap gap-1">
                                {entry.roles_removed?.length > 0 ? (
                                  entry.roles_removed.map((r: string) => (
                                    <span key={r} className="inline-flex items-center gap-0.5 text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium whitespace-nowrap">
                                      <span className="text-red-500 font-bold">−</span>{r}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3.5">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                                addedCount > 0 && removedCount > 0
                                  ? 'bg-blue-100 text-blue-700'
                                  : addedCount > 0
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : removedCount > 0
                                  ? 'bg-red-100 text-red-700' : 'bg-muted text-muted-foreground'
                              }`}>
                                {diffSummary}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── EDIT USER SLIDE-OUT DRAWER ── */}
        {drawerUser && (
          <>
            <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={closeEditDrawer} />
            <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-card shadow-2xl border-l border-border flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#1C2E5E] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold font-heading">{drawerUser.avatar}</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground">Edit User Profile</h3>
                    <p className="text-xs text-muted-foreground">{drawerUser.email}</p>
                  </div>
                </div>
                <button suppressHydrationWarning onClick={closeEditDrawer} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-standard">
                  <Icon name="XMarkIcon" size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">First Name</label>
                    <input suppressHydrationWarning type="text" value={drawerForm.firstName} onChange={e => setDrawerForm(f => ({ ...f, firstName: e.target.value }))} className="w-full pl-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] focus:border-transparent" placeholder="First name" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Last Name</label>
                    <input suppressHydrationWarning type="text" value={drawerForm.lastName} onChange={e => setDrawerForm(f => ({ ...f, lastName: e.target.value }))} className="w-full pl-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] focus:border-transparent" placeholder="Last name" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Email Address</label>
                  <input suppressHydrationWarning type="email" value={drawerForm.email} onChange={e => setDrawerForm(f => ({ ...f, email: e.target.value }))} className="w-full pl-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] focus:border-transparent" placeholder="user@getnextroof.com" />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Phone Number</label>
                  <input suppressHydrationWarning type="tel" value={drawerForm.phone} onChange={e => setDrawerForm(f => ({ ...f, phone: e.target.value }))} className="w-full pl-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] focus:border-transparent" placeholder="+1 (555) 000-0000" />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Department</label>
                  <input suppressHydrationWarning type="text" value={drawerForm.department} onChange={e => setDrawerForm(f => ({ ...f, department: e.target.value }))} className="w-full pl-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] focus:border-transparent" placeholder="e.g. Project Management, Billing..." />
                </div>

                <div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">Roles <span className="text-red-500">*</span></label>
                    <p className="text-[11px] text-gray-500 mb-2">Select all roles this user should have. Multi-role users see the union of permissions.</p>
                    <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-3 rounded-lg border border-gray-200 bg-gray-50">
                      {ALL_USER_ROLES.map(role => {
                        const checked = drawerRoles.includes(role);
                        return (
                          <label key={role} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors text-[12px] ${
                            checked ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-gray-200 hover:bg-gray-50'
                          }`}>
                            <input type="checkbox" checked={checked} onChange={() => {
                              setDrawerRoles(prev =>
                                checked ? prev.filter(r => r !== role) : [...prev, role]
                              );
                            }} className="w-4 h-4 rounded border-gray-300 text-[#E89A3C] focus:ring-[#E89A3C]" />
                            <span className="font-medium text-gray-700">{ROLE_DISPLAY_LABELS[role] ?? role}</span>
                          </label>
                        );
                      })}
                    </div>
                    {drawerRoles.length === 0 && (
                      <p className="text-[11px] text-amber-700 mt-1">⚠ No roles selected — user will only be able to access /me</p>
                    )}
                    <p className="text-[11px] text-gray-500 mt-1">{drawerRoles.length} role(s) selected</p>
                  </div>
                </div>

                <div className="pt-2 border-t border-border">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Password / Invite</p>
                  <button suppressHydrationWarning onClick={resendInvite} disabled={resendingInvite} className="flex items-center gap-2 px-4 py-2.5 w-full justify-center border border-[#E89A3C] text-[#E89A3C] rounded-lg text-sm font-medium hover:bg-cyan-50 transition-standard disabled:opacity-60 disabled:cursor-not-allowed">
                    {resendingInvite ? (
                      <><div className="w-3.5 h-3.5 border-2 border-[#E89A3C] border-t-transparent rounded-full animate-spin"></div>Sending invite...</>
                    ) : (
                      <><Icon name="EnvelopeIcon" size={16} />Resend Password Invite</>
                    )}
                  </button>
                  <p className="text-xs text-muted-foreground mt-1.5 text-center">Sends a new password setup email to the address above.</p>
                </div>

                <div className="pt-2 border-t border-border space-y-3">
                  <p className="text-xs font-semibold text-muted-foreground">Set Password</p>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">New Password</label>
                    <input suppressHydrationWarning type="password" value={pwForm.password} onChange={e => setPwForm(f => ({ ...f, password: e.target.value }))} className="w-full pl-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] focus:border-transparent" placeholder="Min. 6 characters" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Confirm Password</label>
                    <input suppressHydrationWarning type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} className="w-full pl-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] focus:border-transparent" placeholder="Re-enter password" />
                  </div>
                  {pwError && (
                    <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <Icon name="ExclamationTriangleIcon" size={14} className="flex-shrink-0" />
                      {pwError}
                    </div>
                  )}
                </div>

                {drawerError && (
                  <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <Icon name="ExclamationTriangleIcon" size={14} className="flex-shrink-0" />
                    {drawerError}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30 flex-shrink-0">
                <button suppressHydrationWarning onClick={closeEditDrawer} className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-card border border-border rounded-lg hover:bg-muted transition-standard">
                  Cancel
                </button>
                <button suppressHydrationWarning onClick={savePassword} disabled={pwSaving || !pwForm.password} className="px-4 py-2 text-sm font-medium text-white bg-slate-600 rounded-lg hover:bg-slate-700 transition-standard press-scale flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                  {pwSaving ? (
                    <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Saving...</>
                  ) : (
                    <><Icon name="LockClosedIcon" size={15} />Save Password</>
                  )}
                </button>
                <button suppressHydrationWarning onClick={saveEditDrawer} disabled={drawerSaving || drawerRoles.length === 0} className="px-5 py-2 text-sm font-medium text-white bg-[#E89A3C] rounded-lg hover:bg-[#0599cc] transition-standard press-scale flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                  {drawerSaving ? (
                    <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Saving...</>
                  ) : (
                    <><Icon name="CheckIcon" size={15} variant="solid" />Save Changes</>
                  )}
                </button>
              </div>
            </div>
          </>
        )}

        {/* User Role Edit Modal (shield icon / row click) */}
        {editingUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeUserModal} />
            <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-md border border-border">
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#1C2E5E] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold font-heading">{editingUser.avatar}</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground">{editingUser.name}</h3>
                    <p className="text-xs text-muted-foreground">{editingUser.email}</p>
                  </div>
                </div>
                <button suppressHydrationWarning onClick={closeUserModal} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-standard">
                  <Icon name="XMarkIcon" size={20} />
                </button>
              </div>

              <div className="px-6 py-5">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="ShieldCheckIcon" size={16} className="text-[#1C2E5E]" />
                  <p className="text-sm font-semibold text-foreground">Assign Roles</p>
                  <span className="ml-auto text-xs text-muted-foreground">{draftRoles.length} selected</span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">Toggle roles on or off. Users with multiple roles inherit all combined permissions.</p>

                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {roles.map(role => {
                    const isActive = draftRoles.includes(role.name);
                    return (
                      <button key={role.id} suppressHydrationWarning onClick={() => toggleDraftRole(role.name)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-standard text-left ${isActive ? 'border-[#E89A3C] bg-cyan-50/60' : 'border-border hover:border-[#E89A3C]/40 hover:bg-muted/50'}`}>
                        <div className={`w-8 h-8 rounded-lg ${role.bgColor} flex items-center justify-center flex-shrink-0`}>
                          <Icon name="ShieldCheckIcon" size={15} className={role.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>{role.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{role.description}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-standard ${isActive ? 'border-[#E89A3C] bg-[#E89A3C]' : 'border-border bg-card'}`}>
                          {isActive && <Icon name="CheckIcon" size={11} className="text-white" variant="solid" />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {draftRoles.length === 0 && (
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3 flex items-center gap-2">
                    <Icon name="ExclamationTriangleIcon" size={14} className="flex-shrink-0" />
                    At least one role is recommended for this user.
                  </p>
                )}

                {saveError && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3 flex items-center gap-2">
                    <Icon name="ExclamationTriangleIcon" size={14} className="flex-shrink-0" />
                    {saveError}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30 rounded-b-2xl">
                <button suppressHydrationWarning onClick={closeUserModal} className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-card border border-border rounded-lg hover:bg-muted transition-standard">
                  Cancel
                </button>
                <button suppressHydrationWarning onClick={saveUserRoles} disabled={savingRoles} className="px-4 py-2 text-sm font-medium text-white bg-[#E89A3C] rounded-lg hover:bg-[#0599cc] transition-standard press-scale flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                  {savingRoles ? (
                    <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Saving...</>
                  ) : (
                    <><Icon name="CheckIcon" size={15} variant="solid" />Save Changes</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Credentials Modal */}
        <TempPasswordModal data={resetCredentialsModal} onClose={() => setResetCredentialsModal(null)} />
      </div>
    </AppLayout>
  );
}

interface ModuleCardProps {
  module: Module;
  isSelected: boolean;
  onClick: () => void;
}

function ModuleCard({ module, isSelected, onClick }: ModuleCardProps) {
  return (
    <div
      onClick={onClick}
      className={`bg-card border rounded-xl p-4 cursor-pointer transition-standard hover:shadow-md ${
        isSelected ? 'border-[#E89A3C] shadow-md ring-1 ring-[#E89A3C]/20' : 'border-border hover:border-[#E89A3C]/40'
      } ${module.status === 'coming_soon' ? 'opacity-80' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
          module.status === 'active' ? 'bg-[#E89A3C]/10' : 'bg-muted'
        }`}>
          <Icon name={module.icon as any} size={20} className={module.status === 'active' ? 'text-[#E89A3C]' : 'text-muted-foreground'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-heading font-semibold text-foreground text-sm">{module.label}</h3>
            {module.status === 'active' ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium flex-shrink-0">Active</span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium flex-shrink-0">{module.estimatedDate}</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-4">{module.description}</p>
          <div className="flex items-center gap-2 flex-wrap">
            {module.roles.slice(0, 3).map(r => (
              <span key={r} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#1C2E5E]/10 text-[#1C2E5E] font-medium">{r}</span>
            ))}
            {module.roles.length > 3 && (
              <span className="text-[10px] text-muted-foreground">+{module.roles.length - 3}</span>
            )}
          </div>
        </div>
        {module.status === 'active' && (
          <Link href={module.path} onClick={e => e.stopPropagation()} className="flex-shrink-0 p-1.5 text-[#E89A3C] hover:bg-cyan-50 rounded-md transition-standard" title={`Open ${module.label}`}>
            <Icon name="ArrowTopRightOnSquareIcon" size={16} />
          </Link>
        )}
      </div>
    </div>
  );
}